import React from 'react';
import { Portal, Box, Typography, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/** Hotels-only full-height sheet — layout mirrors Flight mobile Travellers & Class (chip rows + footer). */
function HotelMobileRoomsGuestsSheet({
  open,
  onClose,
  rooms,
  onUpdateRoom,
  onUpdateChildAge,
  onAddRoom,
  onRemoveRoom,
  footerLine1,
  footerLine2,
}) {
  if (!open) return null;

  return (
    <Portal>
      <Box
        className="mobile-travellers-sheet-backdrop open"
        data-hotel-rooms-guests-sheet
        onClick={onClose}
        aria-hidden
      />
      <Box
        className="mobile-travellers-sheet open hotel-mobile-guests-sheet"
        data-hotel-rooms-guests-sheet
        role="dialog"
        aria-modal="true"
        aria-labelledby="hotel-rooms-guests-title"
      >
        <Box className="mobile-travellers-sheet-header">
          <IconButton type="button" size="small" onClick={onClose} aria-label="Close" sx={{ color: '#20202A', ml: -0.5, flexShrink: 0 }}>
            <CloseIcon sx={{ fontSize: 22 }} />
          </IconButton>
          <Typography
            id="hotel-rooms-guests-title"
            component="h2"
            sx={{
              flex: 1,
              textAlign: 'center',
              fontFamily: 'Host Grotesk, sans-serif',
              fontSize: '1.125rem',
              fontWeight: 700,
              color: '#20202A',
              lineHeight: 1.3,
            }}
          >
            Rooms & Guests
          </Typography>
          <Box sx={{ width: 40, flexShrink: 0 }} aria-hidden />
        </Box>

        <Box className="mobile-travellers-sheet-scroll hotel-mobile-guest-sheet-scroll">
          <Typography className="mobile-travellers-sheet-section-label" component="p">
            Select rooms & guests
          </Typography>

          {rooms.map((room, rIdx) => (
            <Box key={rIdx} className="hotel-mobile-room-block hotel-mobile-guest-picker-section">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '12px' }}>
                <Typography className="hotel-mobile-room-heading">Room {rIdx + 1}</Typography>
                {rooms.length > 1 && (
                  <Box
                    component="button"
                    type="button"
                    onClick={() => onRemoveRoom(rIdx)}
                    sx={{
                      border: 'none',
                      background: 'none',
                      p: 0,
                      fontSize: '13px',
                      fontWeight: 600,
                      fontFamily: 'Host Grotesk',
                      color: '#e53935',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    Remove
                  </Box>
                )}
              </Box>

              <Box className="mobile-travellers-section">
                <Box className="mobile-travellers-section-head">
                  <span className="mobile-travellers-section-title">Adults</span>
                  <span className="mobile-travellers-section-hint">Age 18+</span>
                </Box>
                <Box className="mobile-travellers-num-row hotel-mobile-guest-equal-row" role="group" aria-label={`Room ${rIdx + 1} adults`}>
                  {Array.from({ length: 8 }, (_, i) => i + 1).map((n) => (
                    <button
                      key={`a-${rIdx}-${n}`}
                      type="button"
                      className={`mobile-travellers-num-chip hotel-mobile-guest-chip${room.adults === n ? ' mobile-travellers-num-chip--active hotel-mobile-guest-chip--active' : ''}`}
                      onClick={() => onUpdateRoom(rIdx, 'adults', n)}
                    >
                      {n}
                    </button>
                  ))}
                </Box>
              </Box>

              <Box className="mobile-travellers-section" sx={{ mt: '20px' }}>
                <Box className="mobile-travellers-section-head">
                  <span className="mobile-travellers-section-title">Children</span>
                  <span className="mobile-travellers-section-hint">Age 0–17</span>
                </Box>
                <Box className="mobile-travellers-num-row hotel-mobile-guest-equal-row" role="group" aria-label={`Room ${rIdx + 1} children`}>
                  {[0, 1, 2, 3, 4].map((c) => (
                    <button
                      key={`c-${rIdx}-${c}`}
                      type="button"
                      className={`mobile-travellers-num-chip hotel-mobile-guest-chip${room.children === c ? ' mobile-travellers-num-chip--active hotel-mobile-guest-chip--active' : ''}`}
                      onClick={() => onUpdateRoom(rIdx, 'children', c)}
                    >
                      {c}
                    </button>
                  ))}
                </Box>
              </Box>

              {room.children > 0 && (
                <Box className="hotel-mobile-child-ages-block" sx={{ mt: '16px' }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#545665', fontFamily: 'Host Grotesk', mb: '10px' }}>
                    Children&apos;s ages
                  </Typography>
                  {room.childrenAges.map((age, cIdx) => (
                    <Box key={cIdx} sx={{ mb: '12px' }}>
                      <Typography sx={{ fontSize: '12px', color: '#7E808F', fontFamily: 'Host Grotesk', mb: '6px' }}>
                        Child {cIdx + 1}
                      </Typography>
                      <Box className="mobile-travellers-num-row" sx={{ flexWrap: 'wrap' }} role="listbox" aria-label={`Age child ${cIdx + 1}`}>
                        {Array.from({ length: 18 }, (_, i) => (
                          <button
                            key={`age-${rIdx}-${cIdx}-${i}`}
                            type="button"
                            className={`mobile-travellers-num-chip${age === i ? ' mobile-travellers-num-chip--active' : ''}`}
                            style={{ flex: '0 0 auto', minWidth: '2.5rem' }}
                            onClick={() => onUpdateChildAge(rIdx, cIdx, i)}
                          >
                            {i}
                          </button>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          ))}

          {rooms.length < 6 && (
            <Box
              component="button"
              type="button"
              onClick={onAddRoom}
              sx={{
                width: '100%',
                py: '12px',
                mt: '4px',
                border: '1.5px dashed #E8E9ED',
                borderRadius: '10px',
                textAlign: 'center',
                cursor: 'pointer',
                background: '#fff',
                fontFamily: 'Host Grotesk',
                fontSize: '14px',
                fontWeight: 600,
                color: '#545665',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              + Add room
            </Box>
          )}
        </Box>

        <Box className="mobile-travellers-sheet-footer hotel-mobile-guests-footer">
          <Box className="mobile-travellers-sheet-footer-summary">
            <Typography component="div" className="mobile-travellers-sheet-footer-line1 hotel-mobile-guests-footer-line1">
              {footerLine1}
            </Typography>
            {footerLine2 && (
              <Typography component="div" className="mobile-travellers-sheet-footer-line2 hotel-mobile-guests-footer-dates">
                {footerLine2}
              </Typography>
            )}
          </Box>
          <Button type="button" variant="contained" className="mobile-travellers-sheet-save" onClick={onClose}>
            Save
          </Button>
        </Box>
      </Box>
    </Portal>
  );
}

export default React.memo(HotelMobileRoomsGuestsSheet);
